import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import { ONLINE_ORDERING_ENABLED } from "@/lib/features";

import Landing from "@/pages/Landing";
import MenuPage from "@/pages/Menu";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import Locations from "@/pages/Locations";
import Story from "@/pages/Story";
import Careers from "@/pages/Careers";
import Markets from "@/pages/Markets";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import OrderingPolicy from "@/pages/OrderingPolicy";
import FoodAllergens from "@/pages/FoodAllergens";
import LocationDisclaimer from "@/pages/LocationDisclaimer";
import Accessibility from "@/pages/Accessibility";
import EmploymentPoliciesPage from "@/pages/EmploymentPolicies";

import AdminLayout from "@/pages/admin/AdminLayout";
import StoreLayout from "@/pages/admin/StoreLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Stores from "@/pages/admin/Stores";
import CompanyReports from "@/pages/admin/CompanyReports";
import Employees from "@/pages/admin/Employees";
import MenuAdmin from "@/pages/admin/MenuAdmin";
import CareersAdmin from "@/pages/admin/CareersAdmin";
import CareersContent from "@/pages/admin/CareersContent";

import StoreOverview from "@/pages/admin/StoreOverview";
import StoreRestock from "@/pages/admin/StoreRestock";
import StoreSales from "@/pages/admin/StoreSales";
import StoreCustomers from "@/pages/admin/StoreCustomers";
import Orders from "@/pages/admin/Orders";
import Kitchen from "@/pages/admin/Kitchen";
import POS from "@/pages/admin/POS";
import Inventory from "@/pages/admin/Inventory";
import ClockAdmin from "@/pages/admin/ClockAdmin";
import StorePricing from "@/pages/admin/StorePricing";
import StoreCount from "@/pages/admin/StoreCount";
import StoreTasks from "@/pages/admin/StoreTasks";
import StoreQR from "@/pages/admin/StoreQR";
import StoreFeedback from "@/pages/admin/StoreFeedback";
import StoreDocuments from "@/pages/admin/StoreDocuments";
import Training from "@/pages/admin/Training";
import RestockRequests from "@/pages/admin/RestockRequests";
import InventoryCounts from "@/pages/admin/InventoryCounts";
import Customers from "@/pages/admin/Customers";
import CustomerProfile from "@/pages/admin/CustomerProfile";
import Feedback from "@/pages/Feedback";
import EmployeeDetail from "@/pages/admin/EmployeeDetail";
import CompanyFeedback from "@/pages/admin/CompanyFeedback";

const ADMIN_ANY = ["admin", "super_admin", "employee", "manager", "chef", "cashier"];
const ADMIN_ONLY = ["admin", "super_admin"];

function SiteLayout() {
  const loc = useLocation();
  const hideChrome = loc.pathname.startsWith("/admin");
  return (
    <>
      {!hideChrome && <Navbar />}
      <Outlet />
      {!hideChrome && <Footer />}
    </>
  );
}

function App() {
  useEffect(() => {
    const splash = document.getElementById("splash");
    if (!splash) return;
    splash.classList.add("hide");
    const t = setTimeout(() => splash.remove(), 400);
    return () => clearTimeout(t);
  }, []);
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route element={<SiteLayout />}>
              <Route path="/" element={<Navigate to="/menu" replace />} />
              <Route path="/home" element={<Landing />} />
              <Route path="/menu" element={<MenuPage />} />
              <Route path="/cart" element={ONLINE_ORDERING_ENABLED ? <Cart /> : <Navigate to="/menu" replace />} />
              <Route path="/checkout" element={ONLINE_ORDERING_ENABLED ? <Checkout /> : <Navigate to="/menu" replace />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/locations" element={<Locations />} />
              <Route path="/story" element={<Story />} />
              <Route path="/markets" element={<Markets />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/employment-policies" element={<EmploymentPoliciesPage />} />
              <Route path="/feedback/:sid" element={<Feedback />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/ordering-policy" element={<OrderingPolicy />} />
              <Route path="/food-allergens" element={<FoodAllergens />} />
              <Route path="/location-disclaimer" element={<LocationDisclaimer />} />
              <Route path="/accessibility" element={<Accessibility />} />
              <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />

              {/* Company / All-Stores admin */}
              <Route path="/admin" element={<ProtectedRoute roles={ADMIN_ONLY}><AdminLayout /></ProtectedRoute>}>
                <Route index element={<Dashboard />} />
                <Route path="stores" element={<Stores />} />
                <Route path="inventory-counts" element={<InventoryCounts />} />
                <Route path="restock-requests" element={<RestockRequests />} />
                <Route path="customers" element={<Customers />} />
                <Route path="customers/:uid" element={<CustomerProfile />} />
                <Route path="training" element={<Training />} />
                <Route path="feedback" element={<CompanyFeedback />} />
                <Route path="reports" element={<CompanyReports />} />
                <Route path="orders" element={<Orders />} />
                <Route path="employees" element={<Employees />} />
                <Route path="employees/:eid" element={<EmployeeDetail />} />
                <Route path="menu-catalog" element={<MenuAdmin />} />
                <Route path="applications" element={<CareersAdmin />} />
                <Route path="careers-content" element={<CareersContent />} />
                <Route path="settings" element={<div className="p-10 text-neutral-500">Company settings coming soon.</div>} />
              </Route>

              {/* Store-scoped */}
              <Route path="/admin/stores/:sid" element={<ProtectedRoute roles={ADMIN_ANY}><StoreLayout /></ProtectedRoute>}>
                <Route index element={<StoreOverview />} />
                <Route path="orders" element={<Orders />} />
                <Route path="kitchen" element={<Kitchen />} />
                <Route path="pos" element={<POS />} />
                <Route path="inventory" element={<StoreCount />} />
                <Route path="inventory/manage" element={<Inventory />} />
                <Route path="restock" element={<StoreRestock />} />
                <Route path="tasks" element={<StoreTasks />} />
                <Route path="training" element={<Training />} />
                <Route path="documents" element={<StoreDocuments />} />
                <Route path="feedback" element={<StoreFeedback />} />
                <Route path="qr" element={<StoreQR />} />
                <Route path="employees" element={<Employees />} />
                <Route path="employees/:eid" element={<EmployeeDetail />} />
                <Route path="time-clock" element={<ClockAdmin />} />
                <Route path="sales" element={<StoreSales />} />
                <Route path="customers" element={<StoreCustomers />} />
                <Route path="menu" element={<StorePricing />} />
              </Route>

              {/* Catch-all inside /admin: legacy routes (/admin/inventory, /admin/orders, etc.) now redirect to the company dashboard */}
              <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
            </Route>
          </Routes>
          <Toaster position="top-right" />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
